import React, { useState, useEffect, useCallback } from 'react';
import './News.css'
import LoadingSpinner from '../../backend/components/LoadingSpinner'; // Import the loading spinner
import CardGrid from '../../components/card-grid';
import SubscribeForm from '../SubscribeFormFolder/SubscribeForm';

import {
    LazyLoadImage,
    LazyLoadComponent,
} from 'react-lazy-load-image-component';

// Assets
// Background video
const BannerBg = require('./videos/news-page-video.mp4');
const BannerBg_LazyLoadImg = require('./img//news-lazyload.png');

// Images
import newsBg1 from './img/news-bg1.png'

type Post = {
    title: string;
    href: string;
    date: string;
    datetime: string;
    image_url: any;
    new_tab: boolean;
    is_news: boolean;
    is_multimedia: boolean;
};

type MainPost = {
    title: string;
    href: string;
    date: string;
    datetime: string;
    image_url: any;
    description: string;
};

const SkeletonCards = () => {
    const skeletons = Array.from({ length: 3 });

    return (
        <div className="news-skeleton-grid">
            {skeletons.map((_, i) => (
                <div key={i} className="news-skeleton-card">
                    <div className="news-skeleton-image" />
                    <div className="news-skeleton-content">
                        <div className="news-skeleton-line title" />
                        <div className="news-skeleton-line date" />
                    </div>
                </div>
            ))}
        </div>
    );
};

const News = () => {

    // Section for the video and its lazyLoad functionality
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    const [displayMode, setDisplayMode] = useState('all'); // Include 'videos' in the possible states

    const [posts, setPosts] = useState<Post[]>([]);
    const [mainPost, setMainPost] = useState<MainPost[]>([]);
    const [postsLoading, setPostsLoading] = useState<boolean>(true);
    const [mainPostLoading, setMainPostLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [nextPage, setNextPage] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);


    const API_BASE =
        process.env.NODE_ENV === 'development'
            ? 'http://localhost:8000'
            : '';

    const fetchPosts = async (url?: string) => {
        try {
            const response = await fetch(
                url || `${API_BASE}/api/posts/?type=${displayMode}`,
                { method: 'GET' }
            );

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();

            setPosts(prev => url ? [...prev, ...data.results] : data.results);

            // Fix pagination URL
            if (data.next) {
                const nextUrl = new URL(data.next);
                setNextPage(API_BASE + nextUrl.pathname + nextUrl.search);
            } else {
                setNextPage(null);
            }

        } catch (err) {
            if (err instanceof Error) setError(err.message);
            else setError('An unknown error occurred');
        } finally {
            setPostsLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        setPosts([]);
        setNextPage(null);
        setPostsLoading(true);

        fetchPosts();
    }, [displayMode]);

    // Load More handler
    const loadMorePosts = async () => {
        if (!nextPage) return;

        setLoadingMore(true);
        await fetchPosts(nextPage);
    };


    const fetchMainPost = async () => {
        try {
            const response = await fetch('/api/highlightedpost/', {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data: MainPost[] = await response.json();
            setMainPost(data); // Assign the fetched posts to state
        } catch (err) {
            // Type assertion to 'Error' to access the message property
            if (err instanceof Error) {
                setError(err.message); // Handle error
            } else {
                setError('An unknown error occurred'); // Fallback for unknown errors
            }
        } finally {
            setMainPostLoading(false);
        }
    };

    useEffect(() => {
        fetchMainPost();
    }, []); // Empty dependency array to run once on mount

    const handleButtonClick = (mode: any) => {
        setDisplayMode(displayMode === mode ? 'all' : mode);
    };

    const getButtonClass = (mode: string) =>
        displayMode === mode ? 'btn custom-btn selected' : 'btn custom-btn';


    return (
        <div className="home-container">

            <div className="news-hero-section">
                {!isVideoLoaded && (
                    <LazyLoadComponent
                        placeholder={<img src={BannerBg_LazyLoadImg} alt="" />}
                    >
                        <LazyLoadImage
                            src={BannerBg_LazyLoadImg}
                            alt="="
                            className="hero-image3"
                        />
                    </LazyLoadComponent>
                )}

                <video
                    className={`hero-image3 ${isVideoLoaded ? '' : 'hidden'}`}
                    src={BannerBg}
                    autoPlay
                    loop
                    muted
                    playsInline
                    onLoadedData={() => setIsVideoLoaded(true)}
                />

                <div className="dark-overlay"></div> {/* ← Overlay */}

                <div className="news-info-wrapper">
                    <h1>News & Media</h1>
                </div>
            </div>

            <div className='news-overall-wrapper'>
                {mainPostLoading ? (
                    <div className="main-news-loading-wrapper">
                        <LoadingSpinner />
                    </div>
                ) : mainPost && mainPost.length > 0 ? (
                    <div className='main-news-wrapper'>
                        <a
                            className="inside-main-news-image"
                            style={{
                                backgroundImage: `url(${mainPost[0].image_url})`,
                                cursor: 'pointer'
                            }}

                            href={mainPost[0].href}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <div>
                                <h1>{mainPost[0].title}</h1>
                                <p>
                                    {new Date(mainPost[0].datetime).toLocaleString('en-US', {
                                        timeZone: 'America/New_York',
                                        dateStyle: 'long'
                                    })}
                                </p>
                            </div>
                        </a>

                        <div className='post-info'>
                            <h1 className='news-overall-wrapper-h1' style={{ color: '#28AAE1', fontWeight: '700' }}>Top Story</h1>
                            <p>{mainPost[0].description}</p>
                        </div>
                    </div>
                ) : (
                    <p>No main post available.</p>
                )}


            </div>



            <div className='news-card-stack'>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '20px',
                    }}
                >
                    <button
                        className={getButtonClass('all')}
                        onClick={() => handleButtonClick('all')}
                        style={{ marginLeft: '0px' }}
                    >
                        All
                    </button>

                    <button
                        className={getButtonClass('news')}
                        onClick={() => handleButtonClick('news')}
                        style={{ marginLeft: '10px' }}
                    >
                        News
                    </button>
                    <button
                        className={getButtonClass('stories')}
                        onClick={() => handleButtonClick('stories')}
                        style={{ marginLeft: '10px' }}
                    >
                        Stories
                    </button>
                    <button
                        className={getButtonClass('multimedia')}
                        onClick={() => handleButtonClick('multimedia')}
                        style={{ marginLeft: '10px' }}
                    >
                        Multimedia
                    </button>
                </div>

                {postsLoading ? (
                    <SkeletonCards />
                ) : (
                    <>
                        <CardGrid cards={posts} />

                        {nextPage && (
                            <div className='load-more-news-wrapper'>
                                <button
                                    className="btn custom-btn"
                                    onClick={loadMorePosts}
                                    disabled={loadingMore}
                                >
                                    {loadingMore ? 'Loading...' : 'Load More'}
                                </button>
                            </div>
                        )}
                    </>
                )}

            </div>

            <SubscribeForm />
        </div>
    )
}

export default News